"use client";

import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TermsContent() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const handlePrint = () => {
    window.print();
  };
  return <section className="relative min-h-screen bg-background py-16 px-4">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(0, 0%, 0%) 0px, hsl(0, 0%, 0%) 1px, transparent 1px, transparent 2px)"
    }} />

      <div ref={ref} className={`relative mx-auto max-w-3xl transition-all duration-150 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
        {}
        <div className="mb-8 flex justify-end">
          <Button onClick={handlePrint} variant="outline" size="sm" className="transition-all duration-[120ms] ease-in-out hover:scale-[1.03] hover:bg-accent hover:text-accent-foreground">
            <Printer className="size-4" />
            Print
          </Button>
        </div>

        {}
        <article className="space-y-12">
          {}
          <header className="space-y-4">
            <h1 className="text-4xl font-display leading-tight text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.08)] md:text-5xl">
              Terms and Conditions
            </h1>
            <p className="text-base leading-relaxed text-foreground/80">
              Last updated: January 2025
            </p>
          </header>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              1. Introduction
            </h2>
            <p className="text-base leading-loose text-foreground/90">
              Welcome to AB Studios. These Terms and Conditions govern your use
              of our website and services. By accessing or using our services,
              you agree to be bound by these terms. If you do not agree with
              any part of these terms, please do not use our services.
            </p>
          </section>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              2. Services Provided
            </h2>
            <p className="text-base leading-loose text-foreground/90">
              AB Studios offers professional wedding videography and photography
              services in Sydney, NSW. Our services include pre-wedding shoots,
              engagement photography, wedding albums, highlight films, and
              personalized packages tailored to your needs.
            </p>
            <p className="text-base leading-loose text-foreground/90">
              All bookings are subject to availability and must be confirmed
              with a signed contract and deposit payment.
            </p>
          </section>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              3. Booking and Payment
            </h2>
            <h3 className="text-xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.04)]">
              3.1 Booking Process
            </h3>
            <p className="text-base leading-loose text-foreground/90">
              To secure your wedding date, a non-refundable deposit is required
              at the time of booking. The remaining balance must be paid in full
              no later than 14 days before your wedding date.
            </p>
            <h3 className="text-xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.04)]">
              3.2 Cancellation Policy
            </h3>
            <p className="text-base leading-loose text-foreground/90">
              If you need to cancel your booking, please notify us in writing as
              soon as possible. Deposits are non-refundable. Cancellations made
              within 30 days of the wedding date will result in forfeiture of
              the full payment.
            </p>
          </section>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              4. Delivery of Services
            </h2>
            <p className="text-base leading-loose text-foreground/90">
              We will deliver all final edited photographs and videos within 8-12
              weeks of your wedding date. Preview images may be provided within
              2-4 weeks. All digital files will be delivered via secure online
              gallery or USB drive.
            </p>
            <p className="text-base leading-loose text-foreground/90">
              Physical products such as wedding albums will be delivered within
              12-16 weeks of your wedding date, subject to production timelines.
            </p>
          </section>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              5. Copyright and Usage Rights
            </h2>
            <p className="text-base leading-loose text-foreground/90">
              AB Studios retains full copyright of all photographs and videos
              created. Clients are granted a personal, non-commercial license to
              use the images and videos for personal purposes, including sharing
              on social media.
            </p>
            <p className="text-base leading-loose text-foreground/90">
              AB Studios reserves the right to use images and videos from your
              wedding for promotional purposes, including our website, social
              media, and marketing materials, unless otherwise agreed in writing.
            </p>
          </section>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              6. Liability
            </h2>
            <p className="text-base leading-loose text-foreground/90">
              While we take every precaution to ensure the safety of our
              equipment and the quality of our work, AB Studios will not be held
              liable for any loss, damage, or delay caused by circumstances
              beyond our control, including but not limited to equipment failure,
              illness, or natural disasters.
            </p>
            <p className="text-base leading-loose text-foreground/90">
              In the unlikely event that we are unable to fulfill our
              obligations, our liability is limited to a full refund of any
              payments made.
            </p>
          </section>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              7. Contact Information
            </h2>
            <p className="text-base leading-loose text-foreground/90">
              If you have any questions or concerns about these Terms and
              Conditions, please contact us at:
            </p>
            <div className="rounded-md bg-muted p-6 text-base leading-loose text-foreground/90">
              <p className="font-semibold">AB Studios</p>
              <p>Sydney, NSW</p>
              <p>Phone: o411 941 883</p>
              <p>Email: aidan.bartlett.photography@gmail.com</p>
            </div>
          </section>

          {}
          <section className="space-y-4">
            <h2 className="text-2xl font-display leading-snug text-foreground drop-shadow-[0_2px_2px_rgba(0,0,0,0.06)] md:text-3xl">
              8. Changes to Terms
            </h2>
            <p className="text-base leading-loose text-foreground/90">
              AB Studios reserves the right to update or modify these Terms and
              Conditions at any time without prior notice. Any changes will be
              effective immediately upon posting on our website. Your continued
              use of our services after any changes constitutes acceptance of
              the new terms.
            </p>
          </section>
        </article>
      </div>
    </section>;
}